/**
 * Core entity types for the TD Control Centre and the UM Feedback App.
 *
 * Authority: `docs/specs/control-centre/10-data-model.md` (first cut, both apps),
 * with the UM-app note shape reconciled against `docs/specs/um-app/00-overview.md` §12.
 *
 * All eight contradictions listed in `docs/specs/README.md` were **resolved on
 * 2026-08-10**, together with the six rulings R1–R6 of the same evening. Three of those
 * were **revised on 2026-08-11** and the primed forms are the operative ones:
 *
 *   R1′ marks and reports are **one per official per TOURNAMENT**, consolidated across
 *       both competitions and entered once at tournament end. `competition_id` is
 *       removed from `Mark`, `Report` and `ReportOwnership` and stays only where it
 *       genuinely lives — fixtures, appointments and match data.
 *       *(Supersedes R1, which keyed marks and reports per official × competition.)*
 *   R2′ the **TD receives no report and no marks** — no `Mark` or `Report` row ever
 *       points at the TD. ATDs, TOs (Judges included) and UMs: the TD solely (W2).
 *       **Umpires: the TD and the owning UM jointly** — the UM drafts, owns and pushes
 *       (W3), the marks are agreed between the UM and the TD at tournament end, and the
 *       report carries the TD's `td_closing_remarks`.
 *       *(Supersedes R2, which split ownership by reporting line alone.)*
 *   R3  build the locked-note access log (`LockedNoteAccessLog`).
 *   R4″ the TD sees, per UM: counts of notes **recorded**, **pending** (unreleased — draft
 *       and locked COMBINED, never separated), **released**, and the instant of that
 *       manager's **last capture or edit**. The CONTENT of an unreleased note remains
 *       invisible to the TD and to every peer, there is no per-note listing, and drafts are
 *       not told apart from locked notes. Served by one route, `GET /td/um-activity`
 *       (`requireTd`), over migration 050's `app_um_note_activity()`; the shape lives on
 *       `UmActivityRow` in `apps/api/src/repositories/um-activity.ts` and **not on this
 *       file**, because it is an API projection rather than a stored row.
 *       *(Supersedes R4′, which permitted only a bare `has_unreleased_notes` boolean —
 *       "never how many". Alfred, 9 September 2026: "TD should be able to see how many
 *       feedbacks they have recorded, how many pending and how many released. And the last
 *       time they recorded anything." The ceiling that survives — no content, no listing, no
 *       draft/locked split — is enforced by
 *       `packages/shared/tests/promises/locked-note-invisibility.test.ts`.)*
 *   R5  the 21:30 pack-ready email auto-sends, link only, disclosed at onboarding.
 *   R6  magic-link validity 24 hours, no PIN fallback.
 *
 * Conventions taken from 10-data-model.md §1:
 *   - every table carries `id`, `created_at`, `updated_at` and `purge_after`;
 *   - timestamps are ISO-8601 **UTC** strings, rendered Asia/Tokyo (D7);
 *   - dates are `YYYY-MM-DD`; times of day are `HH:mm` (24-hour);
 *   - TD-correctable records carry `source` and `td_added`;
 *   - append-only logs carry `prev_hash` and `row_hash` (D30).
 *
 * British English throughout, per the standing copy rule.
 */
/**
 * Every value of the `pre_tournament_duty_value` enum (migration 001), in the order the
 * editor renders them: the four station checks of ruling CC-Q7(c) first, then the additional
 * responsibilities that are usually an ATD's (spec 04 §B2).
 *
 * Written out once, here, because three places need the same list and must not disagree:
 * the route that validates a posted array, the checkbox editor that offers them, and the
 * enum in the database. A value added to the enum without being added here is offered by
 * nobody; a value here that the enum does not carry is a failed INSERT — which is the loud
 * failure, and the right way round.
 */
export const PRE_TOURNAMENT_DUTY_VALUES = [
    "Passport",
    "Uniform",
    "Stick",
    "GK & PC Equipment",
    "Stations Assignment",
    "Stations-In-Charge",
    "Local Transportation Duty",
    "Social Duty",
    "Flight Duty",
];
/**
 * The roles that hold pre-tournament duties. Spec 04 §B2 is explicit: *"Only TD, ATDs, TOs and
 * Judges do pre-tournament checks; UMs and Umpires are not involved at all."*
 *
 * Migration 003's COMMENT on `pre_tournament_duty` says this is enforced in the application
 * layer rather than by a CHECK, because the role lives on `official` and a role correction must
 * not orphan an existing duty row. This is that enforcement's single source of truth: the route
 * refuses an assignment to any other role, and the profile shows the field as static.
 */
export const PRE_TOURNAMENT_DUTY_ROLES = ["TD", "ATD", "TO", "Judge"];
/** Whether this official's tournament role may hold pre-tournament duties at all. */
export function holdsPreTournamentDuties(role) {
    return PRE_TOURNAMENT_DUTY_ROLES.includes(role);
}
/** Whether `value` is one of the nine enum values. Narrows, so a route can trust it after. */
export function isPreTournamentDutyValue(value) {
    return (typeof value === "string" &&
        PRE_TOURNAMENT_DUTY_VALUES.includes(value));
}
/**
 * THE TWO HALVES OF A TEAM LIST — `team_member_role` (migration 034).
 *
 * **ONE LIST, DECLARED ONCE**, on the pattern {@link HEARING_MATCH_PHASES} sets out below: the
 * array is the single declaration and the union is derived from it, so the route's validator,
 * the repository's query filter and any control that offers the choice all read the same words
 * rather than three copies that drift.
 *
 * Two values and not more. Spec 06 §14 splits a squad exactly this way — *"Players and team
 * officials for the selected team"* — while {@link HearingPersonRole} has three, because a
 * disciplinary record distinguishes a coach. A team LIST does not: TMS states the office in
 * words, and `team_member.official_title` is where those words live. Enumerating `Coach`,
 * `Physiotherapist`, `Team Manager` and `Doctor` would close a vocabulary nobody has fixed.
 */
export const TEAM_MEMBER_ROLES = ["player", "team_official"];
/** The label each role prints under, British English and Title Case per the standing copy rule. */
export const TEAM_MEMBER_ROLE_LABELS = {
    player: "Player",
    team_official: "Team Official",
};
/**
 * The three, in the order migration 054's enum declares them. Iterating this is how a validator
 * stays in step with the type rather than carrying a second, quieter copy of the vocabulary.
 */
export const NOTE_KINDS = ["observation", "general", "debrief_response"];
/** Whether an arbitrary value is one of the three. The route's loud refusal is built on it. */
export function isNoteKind(value) {
    return typeof value === "string" && NOTE_KINDS.includes(value);
}
/* ────────────────────────────────────────────────────────────────────────────
 * 6. Hearings
 * ──────────────────────────────────────────────────────────────────────────── */
/**
 * WHEN AN INCIDENT HAPPENED — `hearing_match_phase` (migration 001, extended by 027).
 *
 * **ONE LIST, DECLARED ONCE.** The vocabulary used to be written out three times — this union,
 * `MATCH_PHASES` in `routes/hearings.ts`, and `PHASES` in `CreateHearingModal.tsx` — which is
 * exactly the shape that drifts: ruling §J's three new values would have had to land in three
 * places, and a form offering a value the route refuses is a form that fails on submit. The
 * array below is the single declaration; the union is derived from it, and both the route's
 * validator and the form's dropdown import it.
 *
 * ORDER IS THE ORDER THE FORM OFFERS THEM: through a match, then off it, then "unknown" last.
 *
 * RULING §J (D-8, 13 August 2026) added the last three.
 *  * `Quarter Break` and `Half-Time` — spec 06 §14 always listed these; migration 001's enum
 *    lagged, so the form could only offer what the database would accept.
 *  * `Off-Pitch / Outside Match Time` — Alfred: *incidents can happen anytime … and in fact off
 *    the pitch outside match time altogether*, his example being a player posting abusive
 *    comments about the officials on social media after a game. It is a VALUE and never a blank
 *    field: "we do not know when" (`Not Specified`) and "we know exactly when, and it was not
 *    during play" are different findings, and on a disciplinary record the difference matters.
 */
export const HEARING_MATCH_PHASES = [
    "1st Quarter",
    "Quarter Break",
    "2nd Quarter",
    "Half-Time",
    "3rd Quarter",
    "4th Quarter",
    "End Of Game",
    "Off-Pitch / Outside Match Time",
    "Not Specified",
];
/**
 * The one phase that asserts the incident was NOT during play.
 *
 * Named rather than spelled out at each use: the route refuses a match minute alongside it, and
 * the form clears and disables the minute field for it, and those two must agree about which
 * value they mean.
 */
export const OFF_PITCH_PHASE = "Off-Pitch / Outside Match Time";
//# sourceMappingURL=types.js.map